import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtGBP, CITIES } from "@/lib/format";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { GitCompareArrows } from "lucide-react";

const HOUSE_COLORS = { "Bonhams": "#D4AF37", "Christie's": "#0EA5E9", "Sotheby's": "#10B981", "Phillips": "#8B5CF6" };

function ChartCard({ title, data, dataKey, fmt }) {
  return (
    <div className="al-card p-4">
      <p className="al-label">{title}</p>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
          <XAxis dataKey="house" tick={{ fill: "#94a3b8", fontSize: 11 }} />
          <YAxis tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={fmt} width={60} />
          <Tooltip contentStyle={{ background: "#121824", border: "1px solid #334155", borderRadius: 8, color: "#e2e8f0" }} formatter={(v) => fmt ? fmt(v) : v} cursor={{ fill: "#ffffff08" }} />
          <Bar dataKey={dataKey} radius={[4, 4, 0, 0]}>
            {data.map((d) => <Cell key={d.house} fill={HOUSE_COLORS[d.house] || "#D4AF37"} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function Comparison() {
  const [artist, setArtist] = useState("");
  const [city, setCity] = useState("");
  const [q, setQ] = useState({ artist: "", city: "" });

  const { data = [] } = useQuery({
    queryKey: ["compare", q],
    queryFn: async () => (await api.get("/compare", { params: { artist: q.artist || undefined, city: q.city || undefined } })).data,
  });

  return (
    <div>
      <p className="text-[11px] font-mono uppercase tracking-widest text-amber-400/90">Market Analytics</p>
      <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3">
        <GitCompareArrows className="w-7 h-7 text-amber-400" /> Cross-House Comparison
      </h1>
      <p className="mt-2 text-sm text-slate-400 max-w-2xl">
        Comparison across Bonhams, Christie's, Sotheby's and Phillips using GBP-normalized records.
        Coverage is limited to the pilot sample (London &amp; New York fine art under £500k) — not a comprehensive global market.
      </p>

      <div className="al-card p-4 mt-6 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]">
          <label className="al-label">Artist</label>
          <input data-testid="compare-artist-input" className="al-input" placeholder="All artists" value={artist} onChange={(e) => setArtist(e.target.value)} />
        </div>
        <div className="min-w-[140px]">
          <label className="al-label">City</label>
          <select data-testid="compare-city-select" className="al-input" value={city} onChange={(e) => setCity(e.target.value)}>
            <option value="">Both</option>{CITIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <button data-testid="compare-apply-button" className="al-btn" onClick={() => setQ({ artist, city })}>Compare</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        {data.map((h) => (
          <div key={h.house} data-testid={`compare-card-${h.house.replace(/[^a-z]/gi, "").toLowerCase()}`} className="al-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: HOUSE_COLORS[h.house] }} />
              <h3 className="font-serif text-lg text-slate-100">{h.house}</h3>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Lots</span><span className="mono-num">{h.lots}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Sold / Unsold</span><span className="mono-num">{h.sold} / {h.unsold}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Sell-through</span><span className="mono-num text-emerald-400">{h.sell_through_rate}%</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Turnover</span><span className="mono-num text-amber-400">{fmtGBP(h.turnover_gbp)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Avg price</span><span className="mono-num">{fmtGBP(h.avg_price_gbp)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Realized/Est</span><span className="mono-num text-cyan-400">{h.avg_realization_ratio != null ? `${h.avg_realization_ratio}×` : "—"}</span></div>
            </div>
          </div>
        ))}
        {data.length === 0 && <div className="col-span-4 al-card p-16 text-center text-slate-500">No data for this selection.</div>}
      </div>

      {data.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
          <ChartCard title="Total Turnover (£)" data={data} dataKey="turnover_gbp" fmt={(v) => "£" + (v / 1000).toFixed(0) + "k"} />
          <ChartCard title="Sell-Through Rate (%)" data={data} dataKey="sell_through_rate" fmt={(v) => v + "%"} />
          <ChartCard title="Avg Price (£)" data={data} dataKey="avg_price_gbp" fmt={(v) => "£" + (v / 1000).toFixed(0) + "k"} />
        </div>
      )}
    </div>
  );
}
